import { useState, useMemo } from 'react';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';
import parseMarkdown from '../../utils/parseMarkdown';

var SAMPLE = '# Markdown Preview\n\nThis is a **live preview** of your *Markdown* text.\n\n## Formatting\n\nYou can use **bold**, *italic*, ~~strikethrough~~, and ==highlighted== text.\n\nInline `code` looks like this.\n\n## Lists\n\n### Unordered\n- First item\n- Second item\n- Third item\n\n### Ordered\n1. Step one\n2. Step two\n3. Step three\n\n### Task List\n- [x] Write the press release\n- [ ] Update the website\n- [ ] Contact the media\n\n## Blockquote\n\n> The best way to predict the future is to create it.\n\n## Code Block\n\n```\n{\n  "firstName": "John",\n  "lastName": "Smith",\n  "age": 25\n}\n```\n\n## Table\n\n| Feature | Status |\n| ------- | ------ |\n| Bold | Supported |\n| Italic | Supported |\n| Tables | Supported |\n\n## Links & Images\n\n[Visit Anthroper](/)\n\n---\n\nH~2~O is water. X^2^ is X squared.';

function MarkdownPreview() {
  const [input, setInput] = useState('');
  var html = useMemo(function () { return parseMarkdown(input); }, [input]);

  return (
    <div className="page-prettifier">
      <SEO title="Markdown Previewer - Live Markdown Preview" description="Free online Markdown previewer with support for tables, task lists, code blocks, and more. Type Markdown and see live HTML preview." path="/markdown-preview" />
      <div className="container">
        <div className="tool-header">
          <h1>Markdown Previewer</h1>
          <p>Type or paste Markdown on the left and see a live preview on the right.</p>
        </div>
        <AdUnit />
        <div className="toolbar">
          <div className="toolbar-right" style={{ width: '100%', justifyContent: 'flex-start' }}>
            <button onClick={function () { setInput(SAMPLE); }} className="btn btn-primary" type="button">Load Example</button>
            <button onClick={function () { setInput(''); }} className="btn btn-secondary" type="button">Clear</button>
          </div>
        </div>
        <div className="editor-grid">
          <div className="editor-pane">
            <div className="pane-header"><span>Markdown</span><span className="char-count">{input.length} chars</span></div>
            <textarea className="editor-textarea" value={input} onChange={function (e) { setInput(e.target.value); }} placeholder="Type your Markdown here..." spellCheck={false} aria-label="Markdown input" />
          </div>
          <div className="editor-pane">
            <div className="pane-header"><span>Preview</span></div>
            <div className="markdown-preview" dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        </div>

        <section className="tool-guide">
          <h2>Markdown Cheatsheet</h2>
          <p>Here is a quick reference for all supported Markdown syntax.</p>

          <h3>Basic Syntax</h3>
          <div className="cheatsheet-table-wrap">
            <table className="cheatsheet-table">
              <thead><tr><th>Element</th><th>Markdown Syntax</th></tr></thead>
              <tbody>
                <tr><td>Heading 1</td><td><code># Heading</code></td></tr>
                <tr><td>Heading 2</td><td><code>## Heading</code></td></tr>
                <tr><td>Heading 3</td><td><code>### Heading</code></td></tr>
                <tr><td>Bold</td><td><code>**bold text**</code></td></tr>
                <tr><td>Italic</td><td><code>*italicized text*</code></td></tr>
                <tr><td>Blockquote</td><td><code>{'> blockquote'}</code></td></tr>
                <tr><td>Ordered List</td><td><code>1. Item</code></td></tr>
                <tr><td>Unordered List</td><td><code>- Item</code></td></tr>
                <tr><td>Inline Code</td><td><code>`code`</code></td></tr>
                <tr><td>Horizontal Rule</td><td><code>---</code></td></tr>
                <tr><td>Link</td><td><code>[title](url)</code></td></tr>
                <tr><td>Image</td><td><code>![alt](image.jpg)</code></td></tr>
              </tbody>
            </table>
          </div>

          <h3>Extended Syntax</h3>
          <div className="cheatsheet-table-wrap">
            <table className="cheatsheet-table">
              <thead><tr><th>Element</th><th>Markdown Syntax</th></tr></thead>
              <tbody>
                <tr><td>Table</td><td><code>| H1 | H2 |</code> (with separator row)</td></tr>
                <tr><td>Fenced Code Block</td><td><code>```code```</code></td></tr>
                <tr><td>Strikethrough</td><td><code>~~text~~</code></td></tr>
                <tr><td>Task List</td><td><code>- [x] Done</code> / <code>- [ ] Todo</code></td></tr>
                <tr><td>Highlight</td><td><code>==highlighted==</code></td></tr>
                <tr><td>Subscript</td><td><code>H~2~O</code></td></tr>
                <tr><td>Superscript</td><td><code>X^2^</code></td></tr>
              </tbody>
            </table>
          </div>

          <h3>Try It Yourself</h3>
          <p>Click the "Load Example" button above to see all these elements in action. The example includes headings, bold, italic, strikethrough, highlighted text, lists, task lists, blockquotes, code blocks, tables, links, subscript, and superscript.</p>
        </section>
      </div>
    </div>
  );
}

export default MarkdownPreview;
